import React, { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { uploadDocument } from "../../redux/file/actions";
import { uploadConfig } from "./uploadConfig";
import {
  downloadTemplate,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";
import { MaterialSymbol } from "@datalake/towercontrol-banner";
import "./UploadPage.css";

const UploadPage = () => {
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const _apiResponse = useSelector((state) =>
    state.apiResponse?.source === "UPLOAD_FILES"
      ? state.apiResponse.message
      : null,
  );

  const groupedOptions = useMemo(() => {
    return uploadConfig.reduce((acc, item) => {
      if (!acc[item.source]) acc[item.source] = [];
      acc[item.source].push(item);
      return acc;
    }, {});
  }, []);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("csv");
  const [includeAllFields, setIncludeAllFields] = useState(false);

  const handleOpenDownloadDialog = () => {
    setDownloadDialogOpen(true);
  };

  const handleDownloadTemplate = () => {
    downloadTemplate(selectedConfig, downloadFormat, !includeAllFields);
    setDownloadDialogOpen(false);
  };
  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    setError("");
    if (fileRejections.length > 0) {
      setError("Invalid file type. Please check the requirements below.");
      return;
    }
    setSelectedFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: !selectedConfig,
    accept: selectedConfig
      ? selectedConfig.acceptedTypes.reduce(
          (acc, curr) => ({ ...acc, [curr]: [] }),
          {},
        )
      : {},
  });

  const handleConfigChange = (e) => {
    const config = uploadConfig.find((c) => c.name === e.target.value);
    setSelectedConfig(config);
    setSelectedFile(null);
    setError("");
  };

  const handleUpload = () => {
    if (selectedFile && selectedConfig) {
      const saml_token = getLocalStorageSAMLToken();
      const infoToken = extractUserInfoFromToken(saml_token);
      const request_user = infoToken.username;
      dispatch(
        uploadDocument(selectedFile, request_user, selectedConfig.uploadPath),
      );
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} justifyContent="center">
        {/* 1. Selection Header */}
        <Grid item xs={12} md={8}>
          <TextField
            select
            fullWidth
            label="Select Document Type"
            value={selectedConfig?.name || ""}
            onChange={handleConfigChange}
            variant="outlined"
          >
            <MenuItem value="" disabled>
              -- Select a Source --
            </MenuItem>
            {Object.keys(groupedOptions).map((source) => [
              <MenuItem
                key={source}
                disabled
                sx={{
                  fontWeight: "bold",
                  opacity: 1,
                  color: "var(--ale-gray-900)",
                }}
              >
                {source}
              </MenuItem>,
              ...groupedOptions[source].map((item) => (
                <MenuItem key={item.name} value={item.name} sx={{ pl: 4 }}>
                  {item.name}
                </MenuItem>
              )),
            ])}
          </TextField>
        </Grid>

        {selectedConfig && (
          <Grid item xs={12} md={10}>
            <Paper elevation={0} className="upload-paper">
              <Typography
                variant="h6"
                gutterBottom
                color="primary"
                sx={{ color: "var(--ale-primary)" }}
              >
                Configuration: {selectedConfig.name}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<MaterialSymbol name="get_app" />}
                onClick={handleOpenDownloadDialog}
              >
                Download Template
              </Button>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Accepted Extensions:</strong>{" "}
                    {selectedConfig.extensionLabel}
                  </Typography>
                </Grid>
                {selectedConfig.delimiter && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>CSV Delimiter:</strong> `
                      {selectedConfig.delimiter}`
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Expected Fields Structure:
              </Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className="table-header-cell">
                      Field Name
                    </TableCell>
                    <TableCell className="table-header-cell">
                      Description
                    </TableCell>
                    <TableCell className="table-header-cell" align="center">
                      Mandatory
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedConfig.fields.map((field) => (
                    <TableRow key={field.name} hover>
                      <TableCell>
                        <code>{field.name}</code>
                      </TableCell>
                      <TableCell>{field.description}</TableCell>
                      <TableCell align="center">
                        {field.mandatory ? "✅" : "❌"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Dropzone Area */}
              <div
                {...getRootProps()}
                className={`dropzone-container ${isDragActive ? "dropzone-active" : ""}`}
              >
                <input {...getInputProps()} />
                <MaterialSymbol
                  name="cloud_upload"
                  sx={{ fontSize: 48, color: "var(--ale-primary)", mb: 1 }}
                />
                {selectedFile ? (
                  <Typography variant="body1">
                    Selected: <strong>{selectedFile.name}</strong>
                  </Typography>
                ) : (
                  <Typography variant="body1">
                    Drag & drop the{" "}
                    <strong>{selectedConfig.extensionLabel}</strong> file here,
                    or click to browse
                  </Typography>
                )}
              </div>

              {error && (
                <Typography
                  color="error"
                  variant="body2"
                  sx={{ textAlign: "center", mb: 2 }}
                >
                  {error}
                </Typography>
              )}

              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Button
                  variant="contained"
                  className="upload-button-brand"
                  onClick={handleUpload}
                  disabled={!selectedFile}
                  startIcon={<MaterialSymbol name="cloud_upload" />}
                >
                  Upload to {selectedConfig.uploadPath}
                </Button>
              </Box>
              {_apiResponse && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    width: "100%",
                    bgcolor: "success.light",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "success.contrastText",
                  }}
                >
                  <Typography variant="subtitle2">Upload Result:</Typography>
                  <Typography variant="body2">{_apiResponse}</Typography>
                </Box>
              )}
              <Dialog
                open={downloadDialogOpen}
                onClose={() => setDownloadDialogOpen(false)}
              >
                <DialogTitle>Download Template</DialogTitle>
                <DialogContent>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Format</InputLabel>
                    <Select
                      value={downloadFormat}
                      label="Format"
                      onChange={(e) => setDownloadFormat(e.target.value)}
                    >
                      <MenuItem value="csv">CSV</MenuItem>
                      <MenuItem value="json">JSON</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeAllFields}
                        onChange={(e) => setIncludeAllFields(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Include all fields (not just mandatory)"
                    sx={{ mt: 2 }}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDownloadDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDownloadTemplate}
                    variant="contained"
                    className="upload-button-brand"
                    startIcon={<MaterialSymbol name="get_app" />}
                  >
                    Download
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UploadPage;
